import React, { useState } from 'react';
import { cn } from '@/utils/cn';

interface LoaderProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: number | string;
  className?: string;
  variant?: 'default' | 'primary' | 'secondary' | 'white';
}

const Loader = ({ size = 24, className, variant = 'primary', ...props }: LoaderProps) => {
  const variantStyles = {
    default: 'border-gray-500',
    primary: 'border-[#3967fe]',
    secondary: 'border-purple-600',
    white: 'border-white'
  };

  return (
    <div className={cn("flex items-center justify-center", className)} {...props}>
      <div 
        className={cn("animate-spin rounded-full border-b-2", variantStyles[variant])}
        style={{ 
          width: typeof size === 'number' ? `${size}px` : size,
          height: typeof size === 'number' ? `${size}px` : size
        }}
      />
    </div>
  );
};

interface ImageLoaderProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  containerClassName?: string;
  loaderSize?: number | string;
  loaderVariant?: 'default' | 'primary' | 'secondary' | 'white';
}

export const ImageLoader = ({
  src,
  alt,
  className,
  containerClassName,
  loaderSize,
  loaderVariant,
  onLoad,
  ...props
}: ImageLoaderProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setLoading(false);
    if (onLoad) onLoad(e);
  };

  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  return (
    <div className={cn("relative overflow-hidden", containerClassName)}>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3967fe]"></div>
        </div>
      )}
      <img
        src={src}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        className={cn(
          "transition-opacity duration-300",
          loading ? "opacity-0" : "opacity-100",
          className
        )}
        {...props}
      />
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 text-gray-400 text-sm">
          Failed to load
        </div>
      )}
    </div>
  );
};

export const FullScreenLoader = ({ className }: LoaderProps) => {
  return (
    <div className={cn("fixed inset-0 z-[9999] flex items-center justify-center bg-white/90 dark:bg-gray-950/90 backdrop-blur-sm", className)}>
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3967fe]"></div>
    </div>
  );
};

export default Loader;
